import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, LabelList } from 'recharts';
import { Eye, MessageCircle, Users } from 'lucide-react';
import { Footer } from '../_shared/Footer';

const useCounter = (end, duration = 1500) => {
    const [count, setCount] = useState(0);
    const ref = useRef(null);
    const done = useRef(false);
    useEffect(() => {
        const obs = new IntersectionObserver(([e]) => {
            if (e.isIntersecting && !done.current) {
                done.current = true;
                const start = performance.now();
                const tick = (now) => {
                    const p = Math.min(1, (now - start) / duration);
                    const eased = 1 - Math.pow(1 - p, 3);
                    setCount(end * eased);
                    if (p < 1) requestAnimationFrame(tick);
                };
                requestAnimationFrame(tick);
            }
        }, { threshold: 0.4 });
        if (ref.current) obs.observe(ref.current);
        return () => obs.disconnect();
    }, [end, duration]);
    return [count, ref];
};

const fadeUp = {
    hidden: { opacity: 0, y: 28 },
    show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] } }),
};

const Tag = ({ children }) => (
    <span className="inline-flex items-center px-3 py-1 rounded-full border border-cyan-400/30 bg-cyan-400/10 text-cyan-300 text-[11px] font-semibold uppercase tracking-[0.18em]">
        {children}
    </span>
);

const Panel = ({ children, className = '', accent = 'cyan' }) => {
    const ring = { cyan: 'hover:border-cyan-400/30', emerald: 'hover:border-emerald-400/30', amber: 'hover:border-amber-400/30', violet: 'hover:border-violet-400/30' }[accent];
    return (
        <div className={`relative rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-xl p-7 md:p-10 transition-colors duration-500 ${ring} ${className}`}>
            {children}
        </div>
    );
};

const KPI = ({ icon: Icon, color, value, suffix = '', label, sub, isText }) => {
    const [v, ref] = useCounter(isText ? 0 : value);
    return (
        <Panel className="text-left">
            <div ref={ref}>
                <div className={`inline-flex p-2.5 rounded-xl mb-5 ${color}`}><Icon size={20} /></div>
                <p className="text-4xl md:text-5xl font-black text-white tracking-tight">
                    {isText ? value : Math.round(v).toLocaleString()}{suffix}
                </p>
                <p className="text-white/80 font-semibold mt-3">{label}</p>
                <p className="text-white/40 text-sm mt-1">{sub}</p>
            </div>
        </Panel>
    );
};

const Section = ({ tag, title, accent = 'cyan', children, footnote, index }) => {
    const bar = { cyan: 'bg-cyan-400', emerald: 'bg-emerald-400', amber: 'bg-amber-400', violet: 'bg-violet-400' }[accent];
    return (
        <motion.section
            className="max-w-4xl mx-auto px-5 py-8"
            variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true, amount: 0.3 }} custom={index}
        >
            <Panel accent={accent}>
                <div className={`absolute left-0 top-10 bottom-10 w-[3px] rounded-full ${bar}`} />
                <div className="pl-3">
                    <Tag>{tag}</Tag>
                    <h2 className="text-2xl md:text-[2rem] font-bold text-white mt-4 mb-4 leading-snug tracking-tight">{title}</h2>
                    <div className="text-white/70 text-[17px] leading-relaxed space-y-4">{children}</div>
                    {footnote && <p className="text-white/30 text-xs mt-6 border-t border-white/10 pt-4">{footnote}</p>}
                </div>
            </Panel>
        </motion.section>
    );
};

const reachData = [
    { name: 'Instagram', views: 15075 },
    { name: 'Facebook', views: 140400 },
];

export default function SourceLab90Day() {
    useEffect(() => {
        const t = 'Source Code Lab, 90-Day Review | SocialSEO';
        document.title = t;
        const setMeta = (sel, attrs) => {
            let el = document.querySelector(sel);
            if (!el) { el = document.createElement('meta'); Object.entries(attrs).forEach(([k, v]) => el.setAttribute(k, v)); document.head.appendChild(el); }
            el.setAttribute('content', t);
        };
        setMeta('meta[property="og:title"]', { property: 'og:title' });
        setMeta('meta[name="twitter:title"]', { name: 'twitter:title' });
    }, []);

    return (
        <div className="font-sans text-white relative min-h-screen" style={{ fontFamily: "'Plus Jakarta Sans', Inter, sans-serif", background: 'radial-gradient(1200px 800px at 70% -10%, rgba(34,211,238,0.10), transparent 60%), radial-gradient(900px 700px at 10% 20%, rgba(59,130,246,0.10), transparent 55%), #050505' }}>
            <style>{`@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&display=swap');`}</style>

            <header className="fixed top-0 left-0 right-0 z-50 p-4 md:p-6 flex justify-between items-center pointer-events-none">
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="font-bold tracking-tight"><span className="text-white">Social</span><span className="text-cyan-300">SEO</span></span>
                </div>
                <div className="bg-white/10 backdrop-blur-lg border border-white/10 px-4 py-2 rounded-full pointer-events-auto">
                    <span className="text-sm font-medium text-white/80">Source Code Lab · 90-Day Review</span>
                </div>
            </header>

            {/* HERO */}
            <section className="max-w-5xl mx-auto px-5 pt-32 md:pt-40 pb-10 text-center">
                <motion.div variants={fadeUp} initial="hidden" animate="show">
                    <Tag>Mar to Jun 2026 · @sourcecodelab</Tag>
                </motion.div>
                <motion.h1 variants={fadeUp} initial="hidden" animate="show" custom={1}
                    className="text-4xl md:text-6xl lg:text-7xl font-black tracking-tighter mt-6 leading-[1.05] text-transparent bg-clip-text bg-gradient-to-r from-white via-cyan-100 to-white">
                    90 days in, the<br />right people found you.
                </motion.h1>
                <motion.p variants={fadeUp} initial="hidden" animate="show" custom={2}
                    className="text-lg md:text-xl text-cyan-100/70 max-w-2xl mx-auto mt-7 leading-relaxed">
                    155,000 views, and the founders who can actually buy an iGaming platform are already in your DMs. Reaching the right few was always the goal, not the biggest number. We got there.
                </motion.p>

                <motion.div variants={fadeUp} initial="hidden" animate="show" custom={3}
                    className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-14 text-left">
                    <KPI icon={Eye} color="bg-cyan-500/20 text-cyan-300" value={155500} label="Total views in 90 days" sub="Instagram + Facebook reels" />
                    <KPI icon={Users} color="bg-violet-500/20 text-violet-300" value={100} suffix="x" label="Reach vs your audience" sub="1,552 followers, 155K+ reached" />
                    <KPI icon={MessageCircle} color="bg-emerald-500/20 text-emerald-300" value="Inbound" isText label="Founders already in your DMs" sub="From your exact market, unprompted" />
                </motion.div>
            </section>

            {/* 1. THE GOAL */}
            <Section index={0} tag="The Goal" accent="cyan" title="This was never about big numbers. It was about the right people.">
                <p>Your buyer is not a casual scroller. There are only so many founders on earth deciding what to build their iGaming operation on. You do not need millions of views to reach them.</p>
                <p>You need the few hundred who actually matter to see you, again and again, until you are the name they trust when their buying window opens. <span className="text-white font-semibold">That is what these 90 days were built to do.</span></p>
            </Section>

            {/* 2. THE PROOF */}
            <Section index={1} tag="The Proof" accent="emerald" title="Founders in your exact market are already messaging you.">
                <p>You told us people reached out after watching a reel. Sit with that. Strangers in the one small market that can actually buy a platform raised their hand, unprompted, off a page with under 1,400 followers.</p>
                <p>That is the right audience landing on target. Not applause from people who will never run an iGaming operation. <span className="text-white font-semibold">The real signal, and it started inside 90 days.</span></p>
            </Section>

            {/* 3. THE HARD PART + BREAKOUT */}
            <Section index={2} tag="The Hard Part" accent="violet" title="Reaching them was the hard part. We did it anyway.">
                <p>This is a narrow B2B niche, a small pool of buyers spread across the world. And we are reaching them from India, targeting an audience sitting abroad. That is one of the hardest things to pull off on social.</p>
                <p>The proof it works: one reel reached <span className="text-white font-semibold">122,000 of the right kind of people, fully organic</span>, roughly 780 times the page's follower count. The content broke out of the follower bubble and into the exact market you sell to.</p>
                <div className="mt-7 rounded-2xl border border-white/10 bg-black/20 p-5">
                    <p className="text-white/50 text-xs uppercase tracking-widest font-semibold mb-3">Views by platform, 90 days</p>
                    <ResponsiveContainer width="100%" height={140}>
                        <BarChart data={reachData} layout="vertical" margin={{ left: 8, right: 56, top: 4, bottom: 4 }}>
                            <XAxis type="number" hide />
                            <YAxis type="category" dataKey="name" axisLine={false} tickLine={false} width={86} tick={{ fill: 'rgba(255,255,255,0.7)', fontSize: 13, fontWeight: 600 }} />
                            <Bar dataKey="views" radius={[6, 6, 6, 6]} barSize={26}>
                                <Cell fill="#22d3ee" />
                                <Cell fill="#3b82f6" />
                                <LabelList dataKey="views" position="right" formatter={(v) => v.toLocaleString()} fill="#fff" fontSize={13} fontWeight={700} />
                            </Bar>
                        </BarChart>
                    </ResponsiveContainer>
                    <p className="text-white/35 text-xs mt-1">Facebook carried the reach, roughly nine to one. That is where the next quarter leans.</p>
                </div>
            </Section>

            {/* 4. THE SYSTEM */}
            <Section index={3} tag="The System" accent="cyan" title="You have not recorded a single video. That is the point.">
                <p>Before this, none of it existed. Now your company has an engine: your face, your authority, on a polished and well produced reel every few days, <span className="text-white font-semibold">without you filming a thing.</span></p>
                <p>It lands on the right people's phones, speaking to the exact problem you solve. A founder-led content presence that used to need a studio, a team, and your time. You spend none of it.</p>
            </Section>

            {/* 5. WHERE WE ARE */}
            <Section index={4} tag="Where We Are" accent="amber" title="On the content you did not love, you are right. We are already on it.">
                <p>Some recent reels were not at the bar, and that is fair. When the people helping us on production changed, the quality slipped. We do not claim your team's level of expertise, others were filling that gap.</p>
                <p>With Ashish back on it, the edits and the deliverables tighten up again. The engine is built, and it is reaching the right audience. This is not the moment to step away from that over a few reels we are already fixing.</p>
                <p className="text-white font-semibold">Things are coming back on track.</p>
            </Section>

            <Footer />
        </div>
    );
}
